/**
 * Inbound referral-code capture (S3 — Loyalty & Marketing Revamp).
 *
 * Captures the *referrer's* code — the one a NEW customer ARRIVES with via
 * `?ref=...` — so it can be attached to the eventual order POST. Loyalty's
 * referral attribution (Plan 2) reads `order.referralCode` server-side; the
 * client only carries it through.
 *
 * This mirrors the storage discipline of `attribution.ts`: persist on landing
 * (read once at bootstrap before the router strips query params), read at
 * checkout, and CLEAR after a successful order so a single arrival attributes
 * exactly once. Unlike marketing attribution, the referral code is a single
 * value (not a touch history) and is intentionally NOT consent-gated — it is a
 * customer-supplied referral, not tracking metadata.
 *
 * NOTE: `referralCode` is not yet on the generated `OrderInput` model; the
 * checkout casts the payload `as unknown as OrderInput` to pass it through
 * (OpenAPI regen is a backend follow-up).
 */

const KEY = "tappya.referral.code.v1";

/**
 * Read `?ref=` from the landing URL and persist it. A blank/missing `ref` is a
 * no-op — it never clears a previously captured code (so a deep-link without
 * the param mid-session doesn't wipe an earlier arrival's referral).
 */
export function captureReferralFromUrl(
  url: URL = new URL(window.location.href),
): void {
  const ref = (url.searchParams.get("ref") || "").trim();
  if (!ref) return;
  try {
    localStorage.setItem(KEY, ref);
  } catch {
    /* storage blocked (private mode / quota) — best-effort, drop silently */
  }
}

/** The captured referral code, or `null` if none was captured. */
export function getReferralCode(): string | null {
  try {
    return localStorage.getItem(KEY) || null;
  } catch {
    return null;
  }
}

/** Clear the captured code — call after a successful order so it attributes once. */
export function clearReferralCode(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}
